<template>
  <div>
    <PageHeader :title="$t('Users')">
      <CreateButton @click="addUserModalOpen = true">{{ $t("AddUser") }}</CreateButton>
    </PageHeader>
    <FullScreenModal :open="addUserModalOpen" @close="addUserModalOpen = false">
      <MerchantProfileForm @success="profileCreated" />
    </FullScreenModal>

    <SearchToolbar
      class="mb-6"
      :placeholder="'Recherche par nom/email'"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      @update-keyword-search="updateKeywordSearch"
      @update-filter-search="updateFilterSearch"
    />

    <Table
      :columns="columns"
      :data="profilesData"
      :is-updatable="true"
      @update="
        (profile) => {
          editProfile = profile;
        }
      "
      :is-deletable="true"
      @delete="deleteProfile"
    >
      <template #salesChannels="{ row }">
        {{ salesChannels(row).slice(0, 2).join(", ") }}
        <template v-if="salesChannels(row).length > 2">
          <i @click="showProfileStores = row">, +{{ salesChannels(row).length - 2 }} autres</i>
        </template>
      </template>

      <template #signedUp="{ row }">
        <FieldSpan
          :style-to-add="
            row.signedUp
              ? 'text-green-800 bg-green-200 border border-green-300'
              : 'text-red-800 bg-red-200 border border-red-300'
          "
          :value="row.signedUp ? 'Actif' : 'Inactif'"
        />
      </template>

      <template #role="{ row }">
        <FieldSpan
          :style-to-add="'bg-blue-100 text-blue-800 border border-blue-300'"
          :value="row.role"
          :translation-prefix="'roles'"
        />
      </template>
    </Table>

    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="fetchProfiles"
    />

    <Modal :open="showProfileStores !== null" @close="showProfileStores = null">
      <ul>
        <li v-for="salesChannel in salesChannels(showProfileStores)" :key="salesChannel">
          {{ salesChannel }}
        </li>
      </ul>
    </Modal>
    <FullScreenModal :open="!!editProfile" @close="editProfile = null">
      <MerchantProfileForm v-if="editProfile" :profile="editProfile" @success="profileUpdated" />
    </FullScreenModal>
  </div>
</template>
<script>
import {
  CreateButton,
  FullScreenModal,
  Modal,
  PageHeader,
  Pagination,
  SearchToolbar,
  Table,
  FieldSpan,
} from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";
import MerchantProfileForm from "@/components/MerchantProfileForm.vue";

export default {
  components: {
    CreateButton,
    FullScreenModal,
    PageHeader,
    SearchToolbar,
    Table,
    Pagination,
    FieldSpan,
    Modal,
    MerchantProfileForm,
  },
  async mounted() {
    await Promise.all([this.fetchStoreOptions(), this.fetchProfiles()]);
  },
  data() {
    return {
      showProfileStores: null,
      addUserModalOpen: false,
      editProfile: null,
      keywords: null,
      availableFilters: { stores: [] },
      selectedFilters: { stores: [] },
    };
  },
  computed: {
    ...mapState("store", ["isLoading"]),
    ...mapState("merchantProfiles", ["index", "currentPage", "totalPages", "totalCount"]),
    ...mapState("auth", ["currentUser"]),
    ...mapGetters("auth", ["hasAccessToAdminDashboard", "isOrganizationAdmin"]),
    profilesData() {
      return this.index.map((profile) => ({
        ...profile,
        isUpdatable: this.canTouch(profile),
        isDeletable: this.canTouch(profile),
      }));
    },
    columns() {
      return [
        {
          label: "Nom",
          field: "fullName",
        },
        {
          label: "Email",
          field: "email",
        },
        {
          label: "Points de vente",
          field: "stores",
          slot: "salesChannels",
        },
        {
          label: "Statut",
          field: "signedUp",
          slot: "signedUp",
        },
        {
          label: "Role",
          field: "role",
          slot: "role",
        },
      ];
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("merchantProfiles", ["fetchIndex", "delete"]),
    ...mapActions("merchantProfiles", { fetchFormOptions: "new" }),
    async fetchStoreOptions() {
      const options = await this.fetchFormOptions();
      this.availableFilters.stores = (options?.stores ?? []).map((store) => ({ label: store.name, value: store.id }));
    },
    async fetchProfiles(page = 1) {
      await this.fetchIndex({
        page,
        keywords: this.keywords,
        storeIds: this.selectedFilters.stores,
        includes: ["stores"],
      });
    },
    async updateKeywordSearch(keywords) {
      this.keywords = keywords;
      await this.fetchProfiles();
    },
    async updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      await this.fetchProfiles();
    },
    async profileCreated() {
      this.addUserModalOpen = false;
      await this.fetchProfiles();
    },
    async deleteProfile(profile) {
      if (!confirm(`Supprimer ${profile.fullName} ?`)) return;

      const success = await this.delete(profile.id);
      if (!success) return;

      await this.fetchProfiles();
      await this.notify({
        category: "simple",
        type: "success",
        title: "Utilisateur supprimé",
      });
    },
    canTouch(profile) {
      if (this.currentUser.email === profile.email) return false;
      if (!this.isOrganizationAdmin && profile.role === "organization_admin") return false;

      return this.hasAccessToAdminDashboard;
    },
    async profileUpdated() {
      this.editProfile = null;
      await this.fetchProfiles();
    },
    salesChannels(row) {
      return row.stores.map((store) => store.salesChannel).flat();
    },
  },
};
</script>
